// src/App.js
import React, { useState, useEffect } from 'react';
import {
  Truck, BookOpen, Gamepad2, Package, Plus, Edit2, Trash2, LogOut,
  Mail, Lock, Eye, EyeOff, Users, Ban, CheckCircle, X,
  PlayCircle, KeyRound, LogIn
} from 'lucide-react';
import { auth, db } from './firebase';
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup,
  signOut, onAuthStateChanged, sendEmailVerification
} from 'firebase/auth';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, setDoc, getDoc
} from 'firebase/firestore';
import { sendMagicLink, completeMagicLinkSignIn } from './passwordless';
import { isDisposableEmail } from './emailUtils';
import RuTubeModal from './RuTubeModal';
import { ROLE_OPTIONS, ROLE_MATRIX } from './roles';
import './i18n'; // Убедитесь, что путь правильный
import { useTranslation } from 'react-i18next';

// ===== Mailto CTA =====
const MAIL_TO = 'project@logistoria.com';
const MAILTO_PRO = `mailto:${MAIL_TO}?subject=${encodeURIComponent('Logistoria PRO — заявка')}&body=${encodeURIComponent(
  'Здравствуйте! Хотим подключить PRO-доступ к Logistoria.\nКомпания/ФИО:\nКонтакты:\nКоличество пользователей:\nКомментарии:\n'
)}`;
const MAILTO_BOARD = `mailto:${MAIL_TO}?subject=${encodeURIComponent('Заказать настольные игры')}&body=${encodeURIComponent(
  'Здравствуйте! Хотим заказать настольные игры Logistoria.\nКомпания/ФИО:\nКонтакты:\nКакие игры интересуют:\nКоличество комплектов:\nКомментарии:\n'
)}`;

// ===== Тарифы =====
// Определение PLANS внутри App, чтобы использовать t()
const PLANS = [
  { id: 'kadena',       title: 'tariffs.kadena.title',                    price: 'tariffs.kadena.price', period: 'tariffs.kadena.period', description: 'tariffs.kadena.description' },
  { id: 'thebeergame',  title: 'tariffs.thebeergame.title',      price: 'tariffs.thebeergame.price', period: 'tariffs.thebeergame.period', description: 'tariffs.thebeergame.description' },
  { id: 'coursesstud',  title: 'tariffs.coursesstud.title', price: 'tariffs.coursesstud.price', period: 'tariffs.coursesstud.period', description: 'tariffs.coursesstud.description' },
];

// ===== Встроенные элементы каталога =====
// Определение INITIAL_GAMES внутри App, чтобы использовать t()
const INITIAL_GAMES = [
  // Board
  { id: 'board-1', title: 'games.board-1.title',        description: 'games.board-1.description',         url: 'https://krossdok.ru',                       category: 'board',  isBuiltIn: true, type: 'link' },
  { id: 'board-2', title: 'games.board-2.title',   description: 'games.board-2.description',      url: 'https://logistoria.com/thebeergame', category: 'board',  isBuiltIn: true, type: 'link' },
  // Online
  { id: 'online-1', title: 'games.online-1.title',               description: 'games.online-1.description', url: 'https://supplychains.surge.sh',               category: 'online', isBuiltIn: true, type: 'link' },
  { id: 'online-2', title: 'games.online-2.title', description: 'games.online-2.description',     url: 'https://beergame.logistoria.com/login.html', category: 'online', isBuiltIn: true, type: 'link' },
  // Courses (PDF)
  { id: 'course-1', title: 'games.course-1.title', description: 'games.course-1.description', url: 'https://drive.google.com/file/d/17NlgDyppZZl3V7ZwHiDk3H-ntKunod0p/view?usp=share_link', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  { id: 'course-2', title: 'games.course-2.title', description: 'games.course-2.description', url: 'https://drive.google.com/file/d/18QxOIhZd4ezKYnmIksqe2jJrO7MyG8_4/view?usp=share_link', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  { id: 'course-3', title: 'games.course-3.title', description: 'games.course-3.description', url: 'https://drive.google.com/file/d/1nd5AixrwR7QSHoV_YyvPni9A_i1fFsxy/view?usp=share_link', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  { id: 'course-4', title: 'games.course-4.title', description: 'games.course-4.description', url: 'https://supplychains.ru', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  { id: 'course-5', title: 'games.course-5.title', description: 'games.course-5.description', url: 'https://drive.google.com/file/d/1AoI_xbl2IPfyuOJn1Vp5N3UO_LeXzXPe/view?usp=share_link', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  { id: 'course-6', title: 'games.course-6.title', description: 'games.course-6.description', url: 'https://expert.ya.ru/expert/projects/83506531485911f09c1172c39c699680', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  { id: 'course-7', title: 'games.course-7.title', description: 'games.course-7.description', url: 'https://drive.google.com/file/d/12U14Hylwor-67PRGdM8SK3plXtL4HlKr/view?usp=share_link', category: 'courses-pro', isBuiltIn: true, type: 'pdf' },
  // ===== Free Games =====
  { id: 'free-game-1', title: 'games.free-game-1.title', description: 'games.free-game-1.description', url: 'https://supplychains.github.io/waremover/', category: 'user', isBuiltIn: true, type: 'link' },
  { id: 'free-game-2', title: 'games.free-game-2.title', description: 'games.free-game-2.description', url: 'https://supplychains.github.io/shipster/', category: 'user', isBuiltIn: true, type: 'link' },
  { id: 'free-game-3', title: 'games.free-game-3.title', description: 'games.free-game-3.description', url: 'https://www.min2win.ru/gms/671.html', category: 'user', isBuiltIn: true, type: 'link' },
  { id: 'free-game-4', title: 'games.free-game-4.title', description: 'games.free-game-4.description', url: 'https://game.cargorun.ru/', category: 'user', isBuiltIn: true, type: 'link' },
  // ===== Videos =====
  { id: 'video-1', title: 'games.video-1.title', description: 'games.video-1.description', url: 'https://rutube.ru/video/1cab611f5fb45a19af734088491136f5/', category: 'rutube', isBuiltIn: true, type: 'rutube' },
  { id: 'video-2', title: 'games.video-2.title', description: 'games.video-2.description', url: 'https://rutube.ru/video/dea90c0d5203897a163fe8f9d175a6d1/', category: 'rutube', isBuiltIn: true, type: 'rutube' },
  { id: 'video-3', title: 'games.video-3.title', description: 'games.video-3.description', url: 'https://rutube.ru/video/4611ac1973c6a96c32d2f100621d81dc/', category: 'rutube', isBuiltIn: true, type: 'rutube' },
];

// Публичные секции доступны любым ролям
const PUBLIC_SECTIONS = new Set(['contacts', 'plans', 'courses-pro']);

// Порядок секций
const CATEGORIES_ORDERED = ['user', 'board', 'courses-pro', 'rutube', 'online', 'courses-stud', 'plans', 'contacts'];

// i18n ключи для названий секций
const SECTION_T_KEYS = {
  'user': 'sections.user',
  'courses-pro': 'sections.materials',
  'board': 'sections.board',
  'rutube': 'sections.rutube',
  'online': 'sections.online',
  'courses-stud': 'sections.coursesStud',
  'plans': 'sections.plans',
  'contacts': 'sections.contacts'
};

// ===== App =====
function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [formData, setFormData] = useState({ title: '', description: '', url: '' });
  const [ruModalOpen, setRuModalOpen] = useState(false);
  const [ruModalUrl, setRuModalUrl] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);

  // Добавляем состояние для выбранного языка
  
  const { t, i18n } = useTranslation();
  const [selectedLng, setSelectedLng] = useState(i18n.language);

  // Синхронизируем состояние selectedLng с i18n.language
  useEffect(() => {
    setSelectedLng(i18n.language);
  }, [i18n.language]);

  // ДЛЯ ОТЛАДКИ - УДАЛИТЕ ПОТОМ
  useEffect(() => {
    console.log('=== I18N DEBUG ===');
    console.log('Current language:', i18n.language);
    console.log('auth.welcome exists:', i18n.exists('auth.welcome'));
    console.log('auth.welcome value:', t('auth.welcome', { name: 'Test' }));
    console.log('intro.dashboard value:', t('intro.dashboard'));
    console.log('sections.user value:', t('sections.user'));
  }, [i18n.language, t]);

  // ===== magic-link завершение =====
  useEffect(() => {
    completeMagicLinkSignIn()
      .then(async (user) => {
        if (user) {
          await ensureUserDoc(user.uid, user.email, user.displayName);
          setCurrentPage('dashboard');
          await loadGames();
        }
      })
      .catch(() => {});
  }, []);

  // ===== следим за auth =====
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const ref = doc(db, 'users', firebaseUser.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const u = snap.data();
          if (u.status === 'active') {
            setCurrentUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              name: u.name,
              role: (u.role || 'user').toLowerCase(),
            });
            setCurrentPage('dashboard');
            await loadGames();
            if ((u.role || '').toLowerCase() === 'admin') await loadUsers();
          } else {
            await signOut(auth);
            showNotification(t('toasts.accountBlocked'), 'error');
          }
        } else {
          const payload = {
            email: firebaseUser.email,
            name: firebaseUser.displayName || (firebaseUser.email ? firebaseUser.email.split('@')[0] : 'User'),
            role: 'user',
            status: 'active',
            createdAt: new Date().toISOString(),
          };
          await setDoc(ref, payload, { merge: true });
          setCurrentUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            name: payload.name,
            role: payload.role,
          });
          setCurrentPage('dashboard');
          await loadGames();
        }
      } else {
        setCurrentUser(null);
        setCurrentPage('login');
      }
      setLoading(false);
    });
    return () => unsub();
  }, [t]); // Зависимость от t не обязательна, если не используется в теле эффекта напрямую

  // ===== helpers =====
  const ensureUserDoc = async (uid, email, displayName) => {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        email,
        name: displayName || (email ? email.split('@')[0] : 'User'),
        role: 'user',
        status: 'active',
        createdAt: new Date().toISOString()
      });
    }
  };
  const loadGames = async () => {
    try {
      const qs = await getDocs(collection(db, 'games'));
      const loaded = qs.docs.map(d => ({ id: d.id, ...d.data() }));
      setGames([...INITIAL_GAMES, ...loaded]);
    } catch {
      setGames(INITIAL_GAMES);
    }
  };
  const loadUsers = async () => {
    try {
      const qs = await getDocs(collection(db, 'users'));
      setUsers(qs.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    }
  };
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ===== auth =====
  const handleLogin = async () => {
    try {
      setLoginError('');
      setVerifyNotice('');
      const cred = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      const user = cred.user;
      if (!user.emailVerified) {
        await signOut(auth);
        setVerifyNotice(t('auth.verifyNotice'));
        return;
      }
      showNotification(t('toasts.welcome'));
    } catch {
      setLoginError(t('errors.badCredentials'));
    }
  };
  const handleRegister = async () => {
    if (!registerName || !registerEmail || !registerPassword) {
      setRegisterError(t('errors.fillAll'));
      return;
    }
    if (registerPassword.length < 6) {
      setRegisterError(t('errors.passwordMin'));
      return;
    }
    if (isDisposableEmail(registerEmail)) {
      setRegisterError(t('errors.disposableEmail'));
      return;
    }
    try {
      setRegisterError('');
      const cred = await createUserWithEmailAndPassword(auth, registerEmail, registerPassword);
      const user = cred.user;
      await setDoc(doc(db, 'users', user.uid), {
        email: registerEmail,
        name: registerName,
        role: 'user',
        status: 'active',
        createdAt: new Date().toISOString()
      });
      await sendEmailVerification(user);
      await signOut(auth);
      setIsRegistering(false);
      setRegisterName('');
      setRegisterEmail('');
      setRegisterPassword('');
      setVerifyNotice(t('auth.verifySent'));
      showNotification(t('auth.verifySent'));
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setRegisterError(t('errors.emailInUse'));
      } else {
        setRegisterError(t('errors.register'));
      }
    }
  };
  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      showNotification(t('toasts.googleOk'));
    } catch {
      showNotification(t('toasts.googleFail'), 'error');
    }
  };
  const handleSendMagicLink = async () => {
    if (!loginEmail) {
      setLoginError(t('errors.provideEmail'));
      return;
    }
    if (isDisposableEmail(loginEmail)) {
      setLoginError(t('errors.disposableEmail'));
      return;
    }
    try {
      await sendMagicLink(loginEmail);
      showNotification(t('toasts.magicLinkSent'));
    } catch {
      showNotification(t('toasts.magicLinkFail'), 'error');
    }
  };
  const handleLogout = async () => {
    await signOut(auth);
    setLoginEmail('');
    setLoginPassword('');
  };
  const resendVerificationEmail = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError(t('errors.provideEmail'));
      return;
    }
    try {
      setResendBusy(true);
      const cred = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      const user = cred.user;
      if (user.emailVerified) {
        showNotification(t('auth.alreadyVerified'));
        await signOut(auth);
        return;
      }
      await sendEmailVerification(user);
      await signOut(auth);
      showNotification(t('auth.verifyResent'));
      setVerifyNotice(t('auth.verifyResent'));
    } catch {
      setLoginError(t('errors.badCredentials'));
    } finally {
      setResendBusy(false);
    }
  };

  // ===== admin users =====
  const handleToggleUserStatus = async (userId) => {
    try {
      const ref = doc(db, 'users', userId);
      const snap = await getDoc(ref);
      const newStatus = (snap.data().status === 'active') ? 'blocked' : 'active';
      await updateDoc(ref, { status: newStatus });
      await loadUsers();
      showNotification(t('toasts.statusChanged'));
    } catch {
      showNotification(t('errors.statusChange'), 'error');
    }
  };
  const handleSetUserRole = async (userId, roleId) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: (roleId || 'user') });
      await loadUsers();
      showNotification(t('toasts.roleUpdated', { role: roleId }));
    } catch {
      showNotification(t('errors.roleUpdate'), 'error');
    }
  };
  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      showNotification(t('errors.deleteSelf'), 'error');
      return;
    }
    if (window.confirm(t('admin.confirmDelete'))) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        await loadUsers();
        showNotification(t('toasts.userDeleted'));
      } catch {
        showNotification(t('errors.deleteUser'), 'error');
      }
    }
  };

  // ===== каталог CRUD =====
  const getCategoryGames = (category) => games.filter(g => g.category === category);
  const handleAddGame = async () => {
    if (!formData.title || !formData.description || !formData.url) {
      showNotification(t('errors.fillAll'), 'error');
      return;
    }
    try {
      await addDoc(collection(db, 'games'), {
        ...formData,
        category: selectedCategory,
        isBuiltIn: false,
        type:
          selectedCategory?.startsWith('courses') ? 'pdf' :
          selectedCategory === 'rutube'            ? 'rutube' : 'link',
        createdAt: new Date().toISOString()
      });
      await loadGames();
      setShowAddModal(false);
      setFormData({ title: '', description: '', url: '' });
      setSelectedCategory(null);
      showNotification(t('toasts.added'));
    } catch {
      showNotification(t('errors.add'), 'error');
    }
  };
  const handleEditGame = async () => {
    if (!formData.title || !formData.description || !formData.url) {
      showNotification(t('errors.fillAll'), 'error');
      return;
    }
    try {
      await updateDoc(doc(db, 'games', editingGame.id), {
        title: formData.title,
        description: formData.description,
        url: formData.url
      });
      await loadGames();
      setEditingGame(null);
      setFormData({ title: '', description: '', url: '' });
      showNotification(t('toasts.updated'));
    } catch {
      showNotification(t('errors.edit'), 'error');
    }
  };
  const handleDeleteGame = async (gameId) => {
    if (window.confirm(t('admin.confirmDeleteItem'))) {
      try {
        await deleteDoc(doc(db, 'games', gameId));
        await loadGames();
        showNotification(t('toasts.deleted'));
      } catch {
        showNotification(t('errors.delete'), 'error');
      }
    }
  };
  const openAddModal = (category) => { setSelectedCategory(category); setShowAddModal(true); };
  const openEditModal = (game) => { setEditingGame(game); setFormData({ title: game.title, description: game.description, url: game.url }); };
  const closeModals = () => { setShowAddModal(false); setEditingGame(null); setShowUsersModal(false); setFormData({ title: '', description: '', url: '' }); setSelectedCategory(null); };
  const openRutube = (url) => { setRuModalUrl(url); setRuModalOpen(true); };
  const openReset = () => { setResetEmail(loginEmail || ''); setResetError(''); setResetSent(false); setResetOpen(true); };
  const doReset = async () => {
    setResetError(''); setResetSent(false);
    try {
      if (!resetEmail) { setResetError(t('errors.provideEmail')); return; }
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
    } catch { setResetError(t('errors.resetFail')); }
  };

  // ===== доступы по роли =====
  const role = (currentUser?.role || 'user').toLowerCase();
  const canAccessCategory = (categoryId) => {
    const cid = categoryId === 'free' ? 'user' : categoryId;
    if (PUBLIC_SECTIONS.has(cid)) return true;
    if (role === 'admin' || role === 'pro') return true;
    const allowed = ROLE_MATRIX[role]?.allowed || [];
    return allowed.includes(cid);
  };
  const canOpenCard = (game) => canAccessCategory(game.category);

  // Функция для переключения языка
  const handleLanguageChange = (lng) => {
    console.log('Changing language to:', lng);
    i18n.changeLanguage(lng).then(() => {
      console.log('Language changed to:', i18n.language);
    });
    document.documentElement.lang = lng;
    // setSelectedLng(lng); // Не обязательно, useEffect обновит это
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Truck className="w-16 h-16 text-blue-600 animate-bounce" />
      </div>
    );
  }

  // ====== LOGIN ======
if (currentPage === 'login') {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'} text-white font-medium`}>
          {notification.message}
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full grid md:grid-cols-2">
        <div className="p-8 md:p-12">
          <div className="flex items-center gap-2 mb-8">
            <Truck className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800">Logistoria</h1>
            {/* Language Switcher - ДОБАВЛЕНО НА СТРАНИЦУ LOGIN */}
            <select
              value={selectedLng} // Привязка к состоянию
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="ml-auto" // Выравнивание по правому краю
            >
              <option value="ru">RU</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="zh">中文</option>
              <option value="fr">FR</option>
            </select>
          </div>
          {!isRegistering ? (
            <>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">{t('auth.signinTitle')}</h2>
              <p className="text-gray-600 mb-6">{t('auth.signinSubtitle')}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder={t('auth.emailPlaceholder')} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type={showPassword ? "text" : "password"} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
                    <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {loginError && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">{loginError}</div>}
                <button onClick={handleLogin} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2">
                  <LogIn className="w-4 h-4" />
                  {t('auth.login')}
                </button>
                <div className="grid grid-cols-1 gap-3">
                  <button onClick={handleGoogleLogin} className="w-full border py-3 rounded-lg flex items-center justify-center gap-2">
                    <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" />
                    {t('auth.loginWithGoogle')}
                  </button>
                  <button onClick={handleSendMagicLink} className="w-full border py-3 rounded-lg">
                    {t('auth.loginMagicLink')}
                  </button>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <button onClick={openReset} className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <KeyRound className="w-4 h-4" />
                    {t('auth.forgot')}
                  </button>
                  <button onClick={() => { setIsRegistering(true); setLoginError(''); }} className="text-blue-600 hover:text-blue-700">
                    {t('auth.toRegister')}
                  </button>
                </div>
                {verifyNotice && (
                  <div className="bg-yellow-50 text-yellow-800 px-4 py-3 rounded-lg text-sm mt-3">
                    <div className="mb-2">{verifyNotice}</div>
                    <button onClick={resendVerificationEmail} className="px-3 py-2 bg-yellow-600 text-white rounded-lg disabled:opacity-60" disabled={resendBusy}>
                      {resendBusy ? '...' : t('auth.resendEmail')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">{t('auth.registerTitle')}</h2>
              <p className="text-gray-600 mb-6">{t('auth.registerSubtitle')}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.name')}</label>
                  <input type="text" value={registerName} onChange={(e) => setRegisterName(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Ivan Ivanov" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder={t('auth.emailPlaceholder')} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type={showRegisterPassword ? "text" : "password"} value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRegister()} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder={t('auth.passwordPlaceholder')} />
                    <button onClick={() => setShowRegisterPassword(!showRegisterPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                      {showRegisterPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {registerError && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">{registerError}</div>}
                <button onClick={handleRegister} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg">{t('auth.register')}</button>
                <div className="text-center">
                  <button onClick={() => { setIsRegistering(false); setRegisterError(''); }} className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                    {t('auth.toLogin')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="hidden md:flex bg-gradient-to-br from-blue-600 to-indigo-700 p-12 flex-col justify-center items-center text-white">
          <Truck className="w-32 h-32 mb-8 opacity-90" />
          <h3 className="text-2xl font-bold mb-4 text-center">{t('intro.headerTitle')}</h3>
          <p className="text-blue-100 text-center">{t('intro.headerSubtitle')}</p>
        </div>
      </div>
      {/* Reset Password Modal */}
      {resetOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold flex items-center gap-2"><KeyRound className="w-5 h-5" /> {t('auth.resetTitle')}</h3>
              <button onClick={() => setResetOpen(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">{t('auth.resetHint')}</p>
            <input type="email" className="w-full border rounded-lg px-3 py-2 mb-3" placeholder={t('auth.emailPlaceholder')} value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
            {resetError && <div className="text-sm text-red-600 mb-2">{resetError}</div>}
            {resetSent && <div className="text-sm text-green-600 mb-2">{t('auth.resetSent')}</div>}
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setResetOpen(false)} className="px-4 py-2 border rounded-lg">{t('common.cancel')}</button>
              <button onClick={doReset} className="px-4 py-2 bg-blue-600 text-white rounded-lg">{t('auth.send')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

  // ====== DASHBOARD ======
  const categories = [
    { id: 'user',          icon: Gamepad2,   bgColor: 'bg-green-500' },
    { id: 'courses-pro',   icon: BookOpen,   bgColor: 'bg-amber-600' },
    { id: 'board',         icon: Package,    bgColor: 'bg-purple-500' },
    { id: 'rutube',        icon: PlayCircle, bgColor: 'bg-emerald-500' },
    { id: 'online',        icon: Gamepad2,   bgColor: 'bg-blue-500' },
    { id: 'courses-stud',  icon: BookOpen,   bgColor: 'bg-orange-500' },
    { id: 'plans',         icon: Mail,       bgColor: 'bg-indigo-500' },
    { id: 'contacts',      icon: Mail,       bgColor: 'bg-teal-500' }
  ].sort((a,b)=> CATEGORIES_ORDERED.indexOf(a.id) - CATEGORIES_ORDERED.indexOf(b.id));

  return (
    <div className="min-h-screen bg-gray-50" key={i18n.language}>
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'} text-white`}>
          {notification.message}
        </div>
      )}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8 text-blue-600" />
            <h1 className="text-xl font-bold">Logistoria</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Language Switcher - СТАЛО УПРАВЛЯЕМЫМ */}
            <select
                value={selectedLng} // Привязка к состоянию
                onChange={(e) => handleLanguageChange(e.target.value)}
            >
              <option value="ru">RU</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="zh">中文</option>
              <option value="fr">FR</option>
            </select>
            {(role === 'admin') && (
              <button onClick={() => setShowUsersModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg">
                <Users className="w-4 h-4" />
                {t('admin.users')}
              </button>
            )}
            <span className="text-gray-600">{currentUser?.name}</span>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800">
              <LogOut className="w-4 h-4" />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold mb-2">{t('auth.welcome', { name: currentUser?.name || '' })}</h2>
        <p className="text-gray-600 mb-8">{t('intro.dashboard')}</p>
        <div className="space-y-12">
          {categories.map(category => {
            const Icon = category.icon;
            const categoryGames = getCategoryGames(category.id);
            const canAccessSec = canAccessCategory(category.id);
            if (!canAccessSec) return null;
            return (
              <section key={category.id} className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${category.bgColor} p-3 rounded-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">{t(SECTION_T_KEYS[category.id])}</h3>
                </div>
                {category.id === 'contacts' ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    <a
                      href="https://t.me/supplychains"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group border rounded-xl bg-white shadow-sm hover:shadow-lg transition overflow-hidden flex items-center gap-4 p-5"
                    >
                      <div className="flex-shrink-0 bg-blue-600 text-white p-3 rounded-full group-hover:bg-blue-700 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512" fill="currentColor" className="w-6 h-6"><path d="M248,8C111,8,0,119,0,256S111,504,248,504,496,393,496,256,385,8,248,8Zm121.8,169.9-36.7,172.9c-2.8,12.6-10.1,15.7-20.5,9.8l-56.7-41.8-27.3,26.3c-3,3-5.5,5.5-11.2,5.5l4-57.5,104.7-94.6c4.6-4.1-1-6.4-7.1-2.3L181,275.6l-55.4-17.3c-12.1-3.8-12.3-12.1,2.7-17.9L360.4,159C369.6,155.3,377.7,161.3,369.8,177.9Z"/></svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-800 mb-1">{t('contacts.newsTitle')}</h4>
                        <p className="text-sm text-gray-600">{t('contacts.newsDesc')}</p>
                        <span className="text-blue-600 font-medium text-sm mt-2 inline-block group-hover:underline">
                          {t('contacts.newsGo')}
                        </span>
                      </div>
                    </a>
                    <a
                      href="https://wa.me/message/HGCBTCREGJG3L1?src=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group border rounded-xl bg-white shadow-sm hover:shadow-lg transition overflow-hidden flex items-center gap-4 p-5"
                    >
                      <div className="flex-shrink-0 bg-green-600 text-white p-3 rounded-full group-hover:bg-green-700 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" className="w-6 h-6"><path d="M380.9 97.1C339-0.1 221.8-25.5 128 34.4c-47.8 30.4-77.5 82.6-77.5 139.3 0 30.7 8.4 60.4 24.4 86.4L24 480l123.1-49.4c24.7 13.5 52.7 20.6 81.6 20.6 100.4 0 182.4-82 182.4-182.4 0-40.5-13.1-80.4-37.2-113.7zM228.7 418.5c-24.3 0-48.1-6.3-69-18.1l-4.9-2.9-73 29.3 19.5-77.4-3.2-5C84.4 317.7 76 288.3 76 257.6c0-101 82.3-183.3 183.3-183.3 48.9 0 94.9 19 129.5 53.6 34.5 34.5 53.6 80.5 53.6 129.5 0 101-82.3 183.3-183.3 183.3z"/></svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-800 mb-1">{t('contacts.waTitle')}</h4>
                        <p className="text-sm text-gray-600">{t('contacts.waDesc')}</p>
                        <span className="text-green-600 font-medium text-sm mt-2 inline-block group-hover:underline">
                          {t('contacts.waGo')}
                        </span>
                      </div>
                    </a>
                    <a
                      href="mailto:project@logistoria.com"
                      className="group border rounded-xl bg-white shadow-sm hover:shadow-lg transition overflow-hidden flex items-center gap-4 p-5"
                    >
                      <div className="flex-shrink-0 bg-gray-600 text-white p-3 rounded-full group-hover:bg-gray-700 transition">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className="w-6 h-6">
                          <path d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-800 mb-1">{t('contacts.emailTitle')}</h4>
                        <p className="text-sm text-gray-600">{t('contacts.emailDesc')}</p>
                        <span className="text-gray-600 font-medium text-sm mt-2 inline-block group-hover:underline">
                          {t('contacts.emailGo')}
                        </span>
                      </div>
                    </a>
                  </div>
                ) : category.id === 'plans' ? (
                  <div className="space-y-8">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {PLANS.map(plan => (
                        <div key={plan.id} className="border rounded-xl bg-white shadow-sm p-6 text-center hover:shadow-md transition-shadow">
                          <h4 className="text-xl font-semibold mb-2">{t(plan.title)}</h4>
                          <p className="text-gray-800 text-lg font-bold mb-1">{t(plan.price)}</p>
                          {plan.period && <p className="text-gray-500 text-sm mb-2">{t(plan.period)}</p>}
                          <p className="text-gray-600 text-sm mb-4">{t(plan.description)}</p>
                          <a
                            href={`mailto:${MAIL_TO}?subject=${encodeURIComponent('Оформить тариф ' + t(plan.title))}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg inline-block"
                          >
                            {t('plans.order')}
                          </a>
                        </div>
                      ))}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="border rounded-xl bg-white shadow-sm p-6 text-center">
                        <h4 className="text-xl font-semibold mb-2">{t('tariffs.user.title')}</h4>
                        <p className="text-gray-600 text-sm mb-4">{t('tariffs.user.description')}</p>
                        <button disabled className="bg-gray-300 text-white py-2 px-4 rounded-lg cursor-not-allowed">{t('tariffs.user.status')}</button>
                      </div>
                      <div className="border rounded-xl bg-white shadow-sm p-6 text-center">
                        <h4 className="text-xl font-semibold mb-2">{t('tariffs.pro.title')}</h4>
                        <p className="text-gray-600 text-sm mb-4">{t('tariffs.pro.description')}</p>
                        <a href={MAILTO_PRO} className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg inline-block">{t('tariffs.pro.cta')}</a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {categoryGames.map(game => {
                        const openAllowed = canOpenCard(game);
                        return (
                          <div key={game.id} className="border rounded-lg p-5 hover:shadow-lg transition group">
                            <div className="flex items-start justify-between mb-3">
                              {category.id.startsWith('courses')
                                ? <BookOpen className="w-8 h-8" />
                                : category.id === 'rutube'
                                ? <PlayCircle className="w-8 h-8" />
                                : <Gamepad2 className="w-8 h-8" />
                              }
                              {(role === 'admin') && !game.isBuiltIn && (
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100">
                                  <button onClick={() => openEditModal(game)} className="p-1 text-blue-600" title={t('admin.edit')}><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeleteGame(game.id)} className="p-1 text-red-600" title={t('admin.delete')}><Trash2 className="w-4 h-4" /></button>
                                </div>
                              )}
                            </div>
                            <h4 className="font-semibold mb-2">{t(game.title)}</h4>
                            <p className="text-sm text-gray-600 mb-4">{t(game.description)}</p>
                            {openAllowed ? (
                              game.category === 'rutube' ? (
                                <button onClick={() => openRutube(game.url)} className={`inline-block px-4 py-2 ${category.bgColor} text-white rounded-lg hover:opacity-90 text-sm`}>
                                  {t('cta.watchHere')}
                                </button>
                              ) : game.type === 'pdf' ? (
                                <a href={game.url} target="_blank" rel="noopener noreferrer" className={`inline-block px-4 py-2 ${category.bgColor} text-white rounded-lg hover:opacity-90 text-sm`}>
                                  {t('cta.download')}
                                </a>
                              ) : (
                                <a href={game.url} target="_blank" rel="noopener noreferrer" className={`inline-block px-4 py-2 ${category.bgColor} text-white rounded-lg hover:opacity-90 text-sm`}>
                                  {t('cta.open')}
                                </a>
                              )
                            ) : (
                              <span className="inline-block px-4 py-2 bg-gray-300 text-white rounded-lg text-sm cursor-not-allowed">{t('common.unavailable')}</span>
                            )}
                          </div>
                        );
                      })}
                      {(role === 'admin') && (
                        <button onClick={() => openAddModal(category.id)} className="border-2 border-dashed rounded-lg p-5 hover:border-blue-500 flex flex-col items-center justify-center min-h-[200px]">
                          <Plus className="w-12 h-12 text-gray-400 mb-2" />
                          <p className="text-gray-600">{t('admin.add')}</p>
                        </button>
                      )}
                    </div>
                    {(category.id === 'board' || category.id === 'online') && (
                      <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 flex items-center justify-between flex-col sm:flex-row gap-3">
                        <div className="font-medium">{t('order.bannerText')}</div>
                        <a href={MAILTO_BOARD} className="inline-block px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                          {t('cta.orderGame')}
                        </a>
                      </div>
                    )}
                  </>
                )}
              </section>
            );
          })}
        </div>
      </main>
      {(showAddModal || editingGame) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">{editingGame ? t('admin.editItem') : t('admin.addItem')}</h3>
            {!editingGame && selectedCategory === 'rutube' && <div className="mb-3 text-sm text-gray-600">{t('hints.rutube')}</div>}
            {!editingGame && selectedCategory?.startsWith('courses') && <div className="mb-3 text-sm text-gray-600">{t('hints.pdfPath')}</div>}
            <div className="space-y-4">
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder={t('form.title')} />
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg" rows="3" placeholder={t('form.description')} />
              <input type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="https://..." />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeModals} className="flex-1 px-4 py-2 border rounded-lg">{t('common.cancel')}</button>
              <button onClick={editingGame ? handleEditGame : handleAddGame} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg">
                {editingGame ? t('common.save') : t('common.add')}
              </button>
            </div>
          </div>
        </div>
      )}
      {(role === 'admin') && showUsersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">{t('admin.manageUsers')}</h3>
              <button onClick={() => setShowUsersModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {users.map(user => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg mb-4">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('admin.currentRole')}: <span className="font-medium">{(user.role || 'user').toUpperCase()}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      className="text-sm border rounded px-2 py-1"
                      value={(user.role || 'user').toLowerCase()}
                      onChange={(e) => handleSetUserRole(user.id, e.target.value)}
                    >
                      {ROLE_OPTIONS.map(r => (
                        <option key={r.id} value={r.id}>{r.title}</option>
                      ))}
                    </select>
                    <span className={`px-2 py-1 rounded text-xs ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {user.status === 'active' ? t('status.active') : t('status.blocked')}
                    </span>
                    <button onClick={() => handleToggleUserStatus(user.id)} className="p-2 text-orange-600" title={t('admin.blockUnblock')}>
                      {user.status === 'active' ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    </button>
                    {user.id !== currentUser?.id && (
                      <button onClick={() => handleDeleteUser(user.id)} className="p-2 text-red-600" title={t('admin.delete')}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t">
              <button onClick={() => setShowUsersModal(false)} className="w-full px-4 py-2 border rounded-lg">{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
      {/* RuTube modal */}
      <RuTubeModal open={ruModalOpen} onClose={() => setRuModalOpen(false)} videoUrl={ruModalUrl} />
    </div>
  );
}

export default App;